import './FeatureMapping.scss';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
  KeyboardEvent
} from 'react';
import { Nav } from 'react-bootstrap';
import { MdRefresh, MdSave, MdSettings } from 'react-icons/md';
import { formatNumber, formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { FeatureMappingRow, SegmentMap } from '@blisspointmedia/bpm-types/dist/MMMFeatureMapping';
import { capitalizeString } from '@blisspointmedia/bpm-types/dist/StyleUtils';
import { AlertDialog } from '../../../Components/AlertDialog';
import { Button } from '../../../Components/Button/Button';
import { Design, Size } from '../../../Components/designConstants';
import { DownloadDropdown } from '../../../Components/DownloadDropdown';
import { FilterBar } from '../../../Components/FilterBar/FilterBar';
import { IconButton } from '../../../Components/IconButton/IconButton';
import { Page } from '../../../Components/Page/Page';
import { FullPageSpinner } from '../../../Components/Spinner/Spinner';
import { useSetError } from '../../../redux/modals';
import { downloadJSONToCSV } from '../../../utils/download-utils';
import { findFetchError } from '../../../utils/fetch-error';
import { awaitJSON, SegmentationMappingLambdaFetch } from '../../../utils/fetch-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { useStateFunction } from '../../../utils/hooks/useData';
import { FeatureMappingSpendSummary } from './FeatureMappingSpendSummary';
import {
  EditsMap,
  EditsMapAction,
  FeatureMappingTable,
  MappingRow,
  OutputFeatureData
} from './FeatureMappingTable';
import { InputSettingsModal, ModelTimeline, normalizeModelTimeline } from './InputSettingsModal';

export const TabOptions = {
  MAPPED: 'Mapped',
  UNMAPPED: 'Unmapped'
} as const;
export type TabOptionsType = (typeof TabOptions)[keyof typeof TabOptions];

const editsMapReducer = (state: EditsMap, action: EditsMapAction) => {
  const { type, updates = [], comboKeys, segmentName } = action;

  switch (type) {
    case 'UPDATE_SEGMENT_EDIT': {
      const newState = { ...state };

      for (const update of updates) {
        const { rows, edit } = update;
        if (!rows) {
          throw new Error('No row provided for updating segment edit operation.');
        }

        rows.forEach(row => {
          const existingRow = newState[row.comboKey];

          if (!existingRow) {
            newState[row.comboKey] = { ...row, edits: edit } as MappingRow;
          } else {
            const updatedEdits = { ...existingRow.edits, ...edit };

            // Remove edits equivalent to initial values
            for (const segName of Object.keys(updatedEdits)) {
              const segmentEdit = updatedEdits[segName];
              if (segmentEdit === row[segName]) {
                delete updatedEdits[segName];
              }
            }

            newState[row.comboKey] = { ...existingRow, edits: updatedEdits } as MappingRow;

            const rowEdits = newState[row.comboKey]?.edits ?? {};

            // Remove the comboKey if its edits become empty
            if (Object.keys(rowEdits).length === 0) {
              delete newState[row.comboKey];
            }
          }
        });
      }

      return newState;
    }
    case 'DELETE_SEGMENT_EDIT': {
      if (!comboKeys) {
        throw new Error('No comboKeys provided for deleting segment edit operation.');
      }

      const newState = { ...state };

      comboKeys.forEach(comboKey => {
        const updatedRow = newState[comboKey];
        const updatedRowEdits = updatedRow?.edits ?? {};

        if (segmentName) {
          delete updatedRowEdits[segmentName];
        }

        if (Object.keys(updatedRowEdits).length === 0) {
          delete newState[comboKey];
        }
      });

      return newState;
    }
    default:
      return state;
  }
};

interface MappedAndUnmappedData {
  mappedRows: MappingRow[];
  unmappedRows: MappingRow[];
}

const parsePrettyName = (name: string) =>
  name
    .split('_')
    .map(word => capitalizeString(word))
    .join(' ');

const MIN_SUMMARY_HEIGHT = 100;
const MIN_TABLE_HEIGHT = 200;
const DEFAULT_SUMMARY_HEIGHT = 200;

const HANDLE_HEIGHT = 32; // must stay in sync with .resize-handle height in SCSS
const CONTROLS_MARGIN_TOP = 16; // 1rem

const useResizePanels = () => {
  const [summaryHeight, setSummaryHeight] = useState(DEFAULT_SUMMARY_HEIGHT);
  const heightRef = useRef(DEFAULT_SUMMARY_HEIGHT);
  const bodyRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const listenerRef = useRef<{ move: (e: MouseEvent) => void; up: () => void } | null>(null);

  // Sync state → CSS variable; runs after every state update (keyboard resize)
  useLayoutEffect(() => {
    bodyRef.current?.style.setProperty('--summary-height', `${summaryHeight}px`);
    heightRef.current = summaryHeight;
  }, [summaryHeight]);

  useEffect(
    () => () => {
      if (listenerRef.current) {
        document.removeEventListener('mousemove', listenerRef.current.move);
        document.removeEventListener('mouseup', listenerRef.current.up);
      }
    },
    []
  );

  const handleMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = heightRef.current;

    const onMouseMove = (ev: MouseEvent) => {
      const containerHeight = bodyRef.current?.clientHeight ?? 0;
      const controlsHeight = controlsRef.current
        ? controlsRef.current.offsetHeight + CONTROLS_MARGIN_TOP
        : 0;
      const maxSummary = containerHeight - MIN_TABLE_HEIGHT - HANDLE_HEIGHT - controlsHeight;
      const clamped = Math.max(
        MIN_SUMMARY_HEIGHT,
        Math.min(maxSummary, startHeight - (ev.clientY - startY))
      );
      // Update CSS variable directly — no React re-render per frame
      bodyRef.current?.style.setProperty('--summary-height', `${clamped}px`);
      heightRef.current = clamped;
    };

    const onMouseUp = () => {
      setSummaryHeight(heightRef.current);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      listenerRef.current = null;
    };

    listenerRef.current = { move: onMouseMove, up: onMouseUp };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowUp') {
      setSummaryHeight(h => Math.min(h + 8, 500));
    } else if (e.key === 'ArrowDown') {
      setSummaryHeight(h => Math.max(h - 8, MIN_SUMMARY_HEIGHT));
    }
  }, []);

  return { summaryHeight, handleMouseDown, handleKeyDown, bodyRef, controlsRef };
};

const FeatureMapping = () => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const { summaryHeight, handleMouseDown, handleKeyDown, bodyRef, controlsRef } = useResizePanels();

  const [data, setData] = useState<MappedAndUnmappedData | null>(null);
  const [inputSegments, setInputSegments] = useState<string[]>([]);
  const [needsRedshiftView, setNeedsRedshiftView] = useState(false);

  const fetchRawData = useCallback(async () => {
    if (cid) {
      try {
        const res = await SegmentationMappingLambdaFetch('/getFeatureMappingInputCombos', {
          params: { company: cid }
        });
        const rawData = await awaitJSON(res);

        // All columns that are not defaults are CUSTOM segments and we need to isolate them.
        const defaultColumns = [
          'combo_key',
          'channel',
          'platform',
          'impressions',
          'spend',
          'percent_impressions',
          'percent_spend',
          'feature_id',
          'Mapped Channel',
          'Mapped Platform',
          'Mapped Tactic',
          'Brand / Non-Brand'
        ];

        const defaultSegments = ['comboKey', 'Channel', 'Platform']; // default inputs for all clients
        const customSegments: string[] = [];
        const segmentPrettyNames: Record<string, string> = {};

        // Parse custom inputs from the data
        if (rawData) {
          for (const key of Object.keys(rawData[0])) {
            if (!defaultColumns.includes(key)) {
              if (!segmentPrettyNames[key]) {
                segmentPrettyNames[key] = parsePrettyName(key);
              }
              customSegments.push(key);
            }
          }
        }

        const prettyCustomSegments = customSegments.map(
          segment => segmentPrettyNames[segment] || segment
        );

        const allSegments = defaultSegments.concat(prettyCustomSegments);

        setInputSegments(allSegments);

        const prettyNamedData = rawData.map(row => {
          const prettyRow = {
            ...row,
            comboKey: row.combo_key,
            Channel: row.channel,
            Platform: row.platform
          };
          for (const segment of customSegments) {
            prettyRow[segmentPrettyNames[segment]] = row[segment];
          }
          return prettyRow;
        });

        // Split the data between mapped rows (that have a feature ID already) and unmapped ones.
        const organizedData: MappedAndUnmappedData = {
          mappedRows: [] as MappingRow[],
          unmappedRows: [] as MappingRow[]
        };
        prettyNamedData.forEach(row => {
          if (row.feature_id) {
            organizedData.mappedRows.push(row);
          } else {
            organizedData.unmappedRows.push(row);
          }
        });

        setData(organizedData);
      } catch (e) {
        // No input table for this client yet (the lambda returns a 404): prompt user to set up
        // input segments to create it. 'does not exist' covers the older raw error text.
        if (findFetchError(e)?.status === 404 || e.message.includes('does not exist')) {
          setNeedsRedshiftView(true);
          return;
        }

        setError({ message: e.message, reportError: e });
      }
    }
  }, [cid, setError]);

  useEffect(() => {
    if (data) return;
    (async () => {
      await fetchRawData();
    })();
  }, [cid, data, fetchRawData]);

  const [outputSegments, setOutputSegments] = useState<OutputFeatureData[] | undefined>();

  // Get existing custom segments
  useEffect(() => {
    try {
      if (!outputSegments) {
        (async () => {
          const res = await SegmentationMappingLambdaFetch('/getFeatureMappingOutputSegments', {
            params: { company: cid }
          });
          const segData = await awaitJSON(res);
          setOutputSegments(segData);
        })();
      }
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  }, [cid, outputSegments, setError]);

  const [mappedEditsMap, dispatchMappedEditsMap] = useReducer(editsMapReducer, {} as EditsMap);
  const [unmappedEditsMap, dispatchUnmappedEditsMap] = useReducer(editsMapReducer, {} as EditsMap);
  const [selectedRows, setSelectedRows] = useState<Record<string, MappingRow>>({});
  const [activeTab, setActiveTab] = useState<TabOptionsType>(TabOptions.UNMAPPED);

  const spendPerFeature = useMemo(() => {
    if (!outputSegments || !data) {
      return {};
    }

    const result: Record<string, { pctSpend: number; spend: number }> = {};

    const unmappedRows = data.unmappedRows.map(row => {
      const editedRow = unmappedEditsMap[row.comboKey];
      // Unmapped rows have no original output-segment values, so undefined is expected
      // here for any segment the user hasn't filled in yet. The completeUnmappedRows
      // filter below uses Boolean(value) to separate complete vs incomplete rows.
      const segmentMap: Record<string, string | number | undefined> = {};
      outputSegments.forEach(segment => {
        const { segment_name, segment_id } = segment;
        segmentMap[segment_id] = editedRow?.edits?.[segment_name]?.value_name;
      });
      return { comboKey: row.comboKey, segmentMap, pctSpend: row.percent_spend, spend: row.spend };
    });

    const completeUnmappedRows = unmappedRows.filter(mapping =>
      Object.values(mapping.segmentMap).every(value => Boolean(value))
    );

    const mappedRows = data.mappedRows.map(row => {
      const editedRow = mappedEditsMap && mappedEditsMap[row.comboKey];
      const segmentMap: SegmentMap = {};
      outputSegments.forEach(segment => {
        const { segment_name, segment_id } = segment;
        segmentMap[segment_id] = editedRow
          ? (editedRow.edits && editedRow.edits[segment_name]?.value_name) ||
            editedRow[segment_name]?.value_name ||
            row[segment_name]?.value_name
          : row[segment_name]?.value_name;
      });
      return { comboKey: row.comboKey, segmentMap, pctSpend: row.percent_spend, spend: row.spend };
    });

    const allCompleteRows = [...completeUnmappedRows, ...mappedRows];
    allCompleteRows.forEach(row => {
      const featureName = Object.values(row.segmentMap).join('_');
      const existing = result[featureName] ?? { pctSpend: 0, spend: 0 };
      result[featureName] = {
        pctSpend: existing.pctSpend + Number(row.pctSpend),
        spend: existing.spend + Number(row.spend)
      };
    });

    const incompleteUnmappedRows = unmappedRows.filter(
      mapping => !Object.values(mapping.segmentMap).every(value => Boolean(value))
    );
    const incompletePercentSpend = incompleteUnmappedRows.reduce(
      (acc, row) => acc + Number(row.pctSpend),
      0
    );
    const incompleteSpend = incompleteUnmappedRows.reduce((acc, row) => acc + Number(row.spend), 0);
    result.Unmapped = { pctSpend: incompletePercentSpend, spend: incompleteSpend };

    return result;
  }, [mappedEditsMap, unmappedEditsMap, outputSegments, data]);

  // Number of distinct features currently created — the spendPerFeature keys minus the special
  // 'Unmapped' bucket (which holds spend for rows without a complete mapping, not a real feature).
  const featureCount = useMemo(
    () => Object.keys(spendPerFeature).filter(featureName => featureName !== 'Unmapped').length,
    [spendPerFeature]
  );

  const filterBarOptions = useMemo(() => {
    const inputSegmentOptions = inputSegments
      .filter(segment => segment !== 'comboKey')
      .map(segment => ({ name: segment, label: segment }));

    const outputSegmentOptions =
      outputSegments?.map(segment => ({
        name: segment.segment_name,
        label: segment.segment_name,
        optionsGetter: (line: Record<string, unknown>) => {
          const sv = line[segment.segment_name];
          if (sv && typeof sv === 'object' && 'value_name' in sv) {
            return [String(sv.value_name)];
          }
          return [''];
        }
      })) ?? [];

    return [...inputSegmentOptions, ...outputSegmentOptions];
  }, [inputSegments, outputSegments]);

  const dataToUse = useMemo(
    () =>
      activeTab === TabOptions.UNMAPPED
        ? {
            data: data?.unmappedRows,
            editsMap: unmappedEditsMap,
            dispatchEditsMap: dispatchUnmappedEditsMap
          }
        : {
            data: data?.mappedRows,
            editsMap: mappedEditsMap,
            dispatchEditsMap: dispatchMappedEditsMap
          },
    [activeTab, data, unmappedEditsMap, mappedEditsMap]
  );

  const rowsForFilter = useMemo(
    () =>
      (dataToUse.data ?? []).map(row => {
        const { edits } = dataToUse.editsMap[row.comboKey] ?? {};
        return edits ? { ...row, ...edits } : row;
      }),
    [dataToUse]
  );

  const [filter, setFilter] = useStateFunction<(line: MappingRow) => boolean>(() => true);

  const filteredData = useMemo(
    () => (dataToUse.data ?? []).filter((_, i) => filter(rowsForFilter[i])),
    [filter, dataToUse, rowsForFilter]
  );

  const buildCSVRows = useCallback(
    (rows: MappingRow[], editsMap: EditsMap) => {
      if (!outputSegments) return [];
      const leftHeaders = inputSegments.filter(s => s !== 'comboKey');
      return rows.map(row => {
        const rowToUse = editsMap[row.comboKey] ?? row;
        const record: Record<string, string> = {};
        leftHeaders.forEach(segment => {
          const val = row[segment];
          record[segment] =
            typeof val === 'object' && val !== null
              ? String(val.value_name ?? '')
              : String(val ?? '');
        });
        outputSegments.forEach(({ segment_name }) => {
          const val =
            segment_name in (rowToUse.edits ?? {})
              ? rowToUse.edits?.[segment_name]
              : rowToUse[segment_name];
          record[segment_name] = val?.value_name ?? '';
        });
        record.Spend = String(row.spend ?? '');
        record['Spend %'] =
          row.percent_spend != null ? formatPercent(Number(row.percent_spend), 2) : '';
        return record;
      });
    },
    [outputSegments, inputSegments]
  );

  const downloadCurrentTab = useCallback(() => {
    const rows = buildCSVRows(filteredData, dataToUse.editsMap);
    const isFiltered = filteredData.length < (dataToUse.data?.length ?? 0);
    const suffix = isFiltered ? '-filtered' : '';
    downloadJSONToCSV(rows, `feature-mapping-${cid}-${activeTab.toLowerCase()}${suffix}`);
  }, [buildCSVRows, filteredData, dataToUse, cid, activeTab]);

  const downloadAllRows = useCallback(() => {
    if (!data) return;
    const allEditsMap = { ...mappedEditsMap, ...unmappedEditsMap };

    const mappedRowsForFilter = data.mappedRows.map(row => {
      const { edits } = mappedEditsMap[row.comboKey] ?? {};
      return edits ? { ...row, ...edits } : row;
    });
    const unmappedRowsForFilter = data.unmappedRows.map(row => {
      const { edits } = unmappedEditsMap[row.comboKey] ?? {};
      return edits ? { ...row, ...edits } : row;
    });

    const filteredMapped = data.mappedRows.filter((_, i) => filter(mappedRowsForFilter[i]));
    const filteredUnmapped = data.unmappedRows.filter((_, i) => filter(unmappedRowsForFilter[i]));

    const isFiltered =
      filteredMapped.length + filteredUnmapped.length <
      data.mappedRows.length + data.unmappedRows.length;
    const rows = buildCSVRows([...filteredMapped, ...filteredUnmapped], allEditsMap);
    downloadJSONToCSV(rows, `feature-mapping-${cid}-all${isFiltered ? '-filtered' : ''}`);
  }, [buildCSVRows, data, mappedEditsMap, unmappedEditsMap, cid, filter]);

  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (tab: TabOptionsType) => {
      if (!outputSegments) {
        return;
      }

      setSaving(true);

      try {
        const editsMapToUse = tab === TabOptions.MAPPED ? mappedEditsMap : unmappedEditsMap;

        const mappings: FeatureMappingRow[] = Object.values(editsMapToUse)
          .map(editedRow => {
            const segmentMap: SegmentMap = {};
            outputSegments.forEach(segment => {
              const { segment_name, segment_id } = segment;
              segmentMap[segment_id] =
                (editedRow.edits && editedRow.edits[segment_name]?.value_id) ||
                editedRow[segment_name]?.value_id;
            });
            return { comboKey: editedRow.comboKey, segmentMap };
          })
          .filter(mapping => Object.values(mapping.segmentMap).every(value => Boolean(value))); // Only save rows that have all output segments defined

        await SegmentationMappingLambdaFetch('/saveFeatureMappings', {
          method: 'POST',
          body: {
            company: cid,
            mappings
          }
        });

        await fetchRawData();
        setOutputSegments(undefined);
        setSelectedRows({});
        setSaving(false);
      } catch (e) {
        setSaving(false);
        setError({ message: e.message, reportError: e });
      }
    },
    [cid, fetchRawData, mappedEditsMap, outputSegments, setError, unmappedEditsMap]
  );

  const [refreshing, setRefreshing] = useState(false);
  const [showJobStartedModal, setShowJobStartedModal] = useState(false);

  const handleRefreshInputData = useCallback(async () => {
    if (!cid) return;
    setRefreshing(true);
    try {
      await SegmentationMappingLambdaFetch('/regenerateFeatureMappingInputData', {
        method: 'POST',
        body: { company: cid }
      });
      setShowJobStartedModal(true);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setRefreshing(false);
    }
  }, [cid, setError]);

  const [showInputSettingsModal, setShowInputSettingsModal] = useState(false);
  const [existingInputSegments, setExistingInputSegments] = useState<number[]>([]);
  const [existingModelTimeline, setExistingModelTimeline] = useState<ModelTimeline>({
    startDate: null,
    endDate: null
  });
  const [inputSegmentOptions, setInputSegmentOptions] = useState<{ value: number; name: string }[]>(
    []
  );

  useEffect(() => {
    try {
      if (!existingInputSegments.length) {
        (async () => {
          const res = await SegmentationMappingLambdaFetch('/getFeatureMappingInputSegments', {
            params: { company: cid }
          });
          const { segments, modelTimeline } = await awaitJSON<{
            segments: number[];
            modelTimeline: ModelTimeline | null;
          }>(res);
          setExistingInputSegments(segments);
          setExistingModelTimeline(normalizeModelTimeline(modelTimeline));
        })();
      }
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  }, [cid, setError, existingInputSegments]);

  useEffect(() => {
    try {
      if (!inputSegmentOptions.length) {
        (async () => {
          const res = await SegmentationMappingLambdaFetch('/getCustomSegments', {
            params: { company: cid }
          });
          const segData = await awaitJSON(res);
          const segments = segData.map(item => ({ value: item.segmentId, name: item.segmentName }));
          setInputSegmentOptions(segments);
        })();
      }
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  }, [cid, setError, inputSegmentOptions]);

  return (
    <Page pageType='MMM Feature Mapping' title='MMM Feature Mapping'>
      <div className='feature-mapping'>
        <div className='feature-mapping-nav-container'>
          {needsRedshiftView ? (
            <div />
          ) : (
            <Nav
              activeKey={activeTab}
              onSelect={value => {
                if (!value || value === activeTab) {
                  return;
                }
                setActiveTab(value as TabOptionsType);
                setSelectedRows({});
              }}
            >
              <Nav.Item key={TabOptions.UNMAPPED}>
                <Nav.Link eventKey={TabOptions.UNMAPPED}>{TabOptions.UNMAPPED}</Nav.Link>
              </Nav.Item>
              <Nav.Item key={TabOptions.MAPPED}>
                <Nav.Link eventKey={TabOptions.MAPPED}>{TabOptions.MAPPED}</Nav.Link>
              </Nav.Item>
            </Nav>
          )}
          <div className='feature-mapping-nav-actions'>
            {!needsRedshiftView && (
              <Button
                data-testid='refresh-input-data-button'
                leadingElement={<MdRefresh />}
                onClick={handleRefreshInputData}
                loading={refreshing}
              >
                Refresh Input Data
              </Button>
            )}
            <DownloadDropdown
              menuOptions={[`CSV: ${activeTab}`, 'CSV: All rows']}
              onClickOptions={[downloadCurrentTab, downloadAllRows]}
              disabled={!data || !outputSegments}
              align='end'
              design={Design.BRAND}
            />
            <IconButton size={Size.MD} onClick={() => setShowInputSettingsModal(true)}>
              <MdSettings />
            </IconButton>
          </div>
        </div>
        {existingInputSegments.length && (
          <InputSettingsModal
            show={showInputSettingsModal}
            onClose={() => setShowInputSettingsModal(false)}
            company={cid}
            existingInputSegments={existingInputSegments}
            existingModelTimeline={existingModelTimeline}
            segmentOptions={inputSegmentOptions}
            setExistingInputSegments={setExistingInputSegments}
            setExistingModelTimeline={setExistingModelTimeline}
            onJobStarted={() => setShowJobStartedModal(true)}
          />
        )}
        <AlertDialog
          show={showJobStartedModal}
          onHide={() => setShowJobStartedModal(false)}
          title='Update in progress...'
          body='Your changes won’t be reflected immediately. A job has been kicked off to put them into effect. You’ll receive an email when it’s complete, at which point you can refresh the page to see your updates.'
          primaryAction={{ label: 'Got it' }}
        />
        <div className='feature-mapping-body' ref={bodyRef}>
          {dataToUse.data && outputSegments && !needsRedshiftView ? (
            <>
              <div className='feature-mapping-table-container'>
                <div className='filter-bar-container'>
                  <FilterBar
                    size={Size.LG}
                    options={filterBarOptions}
                    lines={rowsForFilter}
                    onFilter={setFilter}
                    hasAdvanced
                  />
                </div>
                <FeatureMappingTable
                  data={filteredData}
                  inputSegments={inputSegments}
                  outputSegments={outputSegments}
                  editsMap={dataToUse.editsMap}
                  selectedRows={selectedRows}
                  dispatchEditsMap={dataToUse.dispatchEditsMap}
                  setSelectedRows={setSelectedRows}
                />
              </div>
              <div
                className='resize-handle'
                role='slider'
                aria-label='Resize panels'
                aria-orientation='vertical'
                aria-valuenow={summaryHeight}
                aria-valuemin={MIN_SUMMARY_HEIGHT}
                aria-valuemax={500}
                tabIndex={0}
                onMouseDown={handleMouseDown}
                onKeyDown={handleKeyDown}
              >
                <span className='resize-handle-grip' aria-hidden='true' />
              </div>
              <FeatureMappingSpendSummary data={spendPerFeature} />
            </>
          ) : (
            <>
              {needsRedshiftView ? (
                <div
                  className='initialization-message'
                  data-testid='initialization-message'
                >{`Input data has not been initialized for ${cid}.  Please choose input segments in the Settings menu on the top right.`}</div>
              ) : (
                <FullPageSpinner />
              )}
            </>
          )}

          {!needsRedshiftView && (
            <div className='feature-mapping-controls' ref={controlsRef}>
              <span className='unmapped-counter' data-testid='total-features-counter'>
                <span>{formatNumber(featureCount)}</span> Features Created
              </span>
              <span className='controls-divider' aria-hidden='true' />
              <span className='unmapped-counter' data-testid='combos-mapped-counter'>
                <span>{formatNumber(data?.mappedRows?.length ?? 0)}</span>/
                {formatNumber((data?.unmappedRows?.length ?? 0) + (data?.mappedRows?.length ?? 0))}{' '}
                Combos Mapped
              </span>
              <Button
                size={Size.LG}
                data-testid='save-button'
                leadingElement={<MdSave />}
                onClick={() => save(activeTab)}
                loading={saving}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export { FeatureMapping };
